import {
  type ReauthorizationReviewFormSection,
  reauthorizationReviewContextOptions,
  type Task,
  useDecideClinicalReauthorizationMutation,
} from '@cartwheel/api/v1'
import { AuthorizedSessionsCard, Button, LoadingView } from '@cartwheel/components'
import { useQuery } from '@tanstack/react-query'
import { CanceledNotice, TaskDescription } from './shared/task-workspace-primitives'
import { useTaskCompletion } from './shared/use-task-completion'

function FormSection({ section }: { section: ReauthorizationReviewFormSection }) {
  const rows = section.rows.filter((row) => row.value)
  if (rows.length === 0) return null

  return (
    <section className="space-y-2">
      <h4 className="text-sm font-semibold leading-[1.5] text-charcoal">{section.title}</h4>
      <div className="rounded-lg border border-sand bg-white p-4 shadow-sm space-y-3">
        {rows.map((row) => (
          <div key={row.label}>
            <p className="text-xs font-medium text-charcoal-50">{row.label}</p>
            <p className="mt-0.5 text-sm leading-[1.5] text-charcoal">{row.value}</p>
          </div>
        ))}
      </div>
    </section>
  )
}

function ReviewDecisionButtons({
  taskId,
  decide,
  isPending,
}: {
  taskId: string
  decide: (args: { path: { task_id: string }; body: { decision: 'approved' | 'denied' } }) => void
  isPending: boolean
}) {
  return (
    <div className="flex justify-between">
      <Button
        onClick={() => decide({ path: { task_id: taskId }, body: { decision: 'approved' } })}
        disabled={isPending}
      >
        {isPending ? 'Saving…' : 'Approve'}
      </Button>
      <Button
        onClick={() => decide({ path: { task_id: taskId }, body: { decision: 'denied' } })}
        disabled={isPending}
        variant="danger"
      >
        {isPending ? 'Saving…' : 'Deny'}
      </Button>
    </div>
  )
}

function CanceledReauthorizationView({ task }: { task: Task }) {
  return (
    <div className="space-y-8">
      <TaskDescription task={task} />
      <CanceledNotice />
    </div>
  )
}

function CompletedReauthorizationView({ task }: { task: Task }) {
  return (
    <div className="space-y-8">
      <TaskDescription task={task} />
      <section className="space-y-3">
        <h3 className="text-base font-semibold leading-[1.5] text-charcoal">Completed</h3>
        <div className="rounded-lg border border-sand bg-white p-4 shadow-sm">
          <p className="text-sm leading-[1.5] text-charcoal-50">
            A decision has been recorded for this reauthorization.
          </p>
        </div>
      </section>
    </div>
  )
}

export function ReviewSessionLimitReauthorizationWorkspace({
  task,
  canWork = true,
  onClose,
}: {
  task: Task
  canWork?: boolean
  onClose: () => void
}) {
  const { onSuccess, onError } = useTaskCompletion({ taskId: task.id, onClose })

  const contextQuery = useQuery(reauthorizationReviewContextOptions({ path: { task_id: task.id } }))

  const { mutate: decide, isPending } = useDecideClinicalReauthorizationMutation({
    onSuccess: (_data, variables) => {
      const decision = variables.body?.decision
      onSuccess(decision === 'approved' ? 'Reauthorization approved' : 'Reauthorization denied')
    },
    onError: () => onError('Failed to record decision'),
  })

  if (task.status === 'canceled') return <CanceledReauthorizationView task={task} />
  if (task.status === 'completed') return <CompletedReauthorizationView task={task} />

  return (
    <div className="space-y-8">
      <TaskDescription task={task} />

      {contextQuery.isPending && <LoadingView title="Loading reauthorization…" />}

      {contextQuery.isError && (
        <p className="text-sm text-charcoal-50">
          Failed to load reauthorization details. Please try again.
        </p>
      )}

      {contextQuery.data && (
        <>
          <AuthorizedSessionsCard breakdown={contextQuery.data.session_limit} variant="outlined" />

          {contextQuery.data.form_sections.map((section) => (
            <FormSection key={section.title} section={section} />
          ))}

          {canWork && (
            <ReviewDecisionButtons taskId={task.id} decide={decide} isPending={isPending} />
          )}
        </>
      )}
    </div>
  )
}
